import { useEffect, useMemo, useRef } from 'react';
import type { ChatMessage } from '../types/foreman';
import type { UserRole } from '../types/gateway';
import type { ThreadEmail } from '../hooks/useThreadEmails';
import type { CorrectionFormData } from './training';
import { UserMessage, ForemanMessage, SystemMessage } from './messages';
import { EmailMessage } from './EmailMessage';
import { QuickActions } from './QuickActions';
import { getRoleConfig } from '../config/roles';
import styles from './ChatArea.module.css';

interface ChatAreaProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  role: UserRole;
  userName?: string;
  activeSessionId?: string | null;
  /** True when the active thread is the user's auto-seeded `welcome:{username}` thread.
   *  Triggers a richer empty-state intro from Foreman before any chat history exists. */
  isWelcomeThread?: boolean;
  /** Email log for the active thread — rendered inline above chat messages. */
  emails?: ThreadEmail[];
  /** True when the active thread is shared (other members / org-wide / not
   *  mine) — turns on author chips on user messages. */
  isSharedThread?: boolean;
  /** Logged-in username — lets the author chip collapse to "You". */
  currentUsername?: string;
  onCorrect?: (messageId: string, data: CorrectionFormData) => void;
  onAction?: (action: string) => void;
  /** Selected when a quick-action card is clicked from the empty state. */
  onQuickAction?: (prompt: string) => void;
}

function firstName(name?: string): string {
  if (!name) return 'there';
  return name.split(' ')[0] || name;
}

export function ChatArea({
  messages,
  isStreaming,
  role,
  userName,
  activeSessionId,
  isWelcomeThread,
  emails = [],
  isSharedThread,
  currentUsername,
  onCorrect,
  onAction,
  onQuickAction,
}: ChatAreaProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Force a jump-to-bottom on the next content render after a thread switch, so
  // opening a thread always lands on its newest message regardless of where the
  // scroll container happened to be.
  const forceBottomRef = useRef(true);
  const cfg = getRoleConfig(role);
  const headline = cfg.welcomeHeadline.replace('{name}', firstName(userName));

  useEffect(() => {
    forceBottomRef.current = true;
  }, [activeSessionId]);

  // Auto-scroll to the latest message. `messages` is now a stable reference
  // (memoized in useMessages), so this fires on real content changes — not on
  // every unrelated re-render (email poll, ACL refresh). Within a thread we
  // only follow along when the user is already near the bottom, so scrolling up
  // to read backscroll is never yanked away. On a fresh thread open we force it.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const force = forceBottomRef.current;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (force || distanceFromBottom <= 120) {
      bottomRef.current?.scrollIntoView({ behavior: force ? 'auto' : 'smooth' });
      // Only consume the force flag once real content has rendered — history
      // arrives asynchronously after the switch, so an empty first pass must
      // not swallow the forced jump.
      if (messages.length > 0) forceBottomRef.current = false;
    }
  }, [messages, isStreaming]);

  const isEmpty = messages.length === 0 && emails.length === 0;

  // ── Chronological merge of chat messages + thread emails ────────────────────
  // BUG FIX (Foreman "responds ABOVE where someone is chatting"): emails used
  // to render in a block ABOVE all chat messages, so ANY email — including
  // Foreman's OUTBOUND email replies (direction 'out', 204 logged in prod) —
  // was pinned to the very top of the thread, appearing above the live chat the
  // user was actively having. We now interleave emails into the message stream
  // by timestamp WITHOUT reordering the chat messages among themselves (their
  // append order from history + live events is authoritative — many history
  // rows carry no reliable timestamp, so we must NOT sort them by time).
  // Emails arrive ASC by received_at from the server; each is slotted just
  // before the first chat message that is chronologically at-or-after it, and
  // any trailing emails (e.g. a brand-new Foreman reply) land at the bottom.
  type Row =
    | { kind: 'msg'; msg: ChatMessage }
    | { kind: 'email'; email: ThreadEmail };
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    let ei = 0;
    // received_at is epoch SECONDS (INTEGER in auth.db) — normalize to ms.
    const emailMs = emails.map((e) => (e.receivedAt || 0) * 1000);
    // Carry-forward the last good chat timestamp so a missing/NaN chat ts can't
    // yank an email back to the top of the thread.
    let lastMs = 0;
    for (const msg of messages) {
      const parsed = Date.parse(msg.timestamp);
      const mMs = Number.isNaN(parsed) ? lastMs : parsed;
      lastMs = mMs;
      while (ei < emails.length && emailMs[ei] <= mMs) {
        out.push({ kind: 'email', email: emails[ei] });
        ei++;
      }
      out.push({ kind: 'msg', msg });
    }
    while (ei < emails.length) {
      out.push({ kind: 'email', email: emails[ei] });
      ei++;
    }
    return out;
  }, [messages, emails]);

  return (
    <div
      ref={scrollRef}
      className={`${styles.chatArea} ${isEmpty ? styles.chatAreaEmpty : ''}`}
      data-accent={cfg.accent}
      data-print-region="chat"
    >
      <div className={`${styles.messages} ${isEmpty ? styles.messagesEmpty : ''}`}>
        {isEmpty && (
          <div className={styles.welcome}>
            <div className={styles.welcomeBadge}>
              <span className={styles.welcomeIcon}>{cfg.icon}</span>
              <span className={styles.welcomeBadgeLabel}>{cfg.label}</span>
            </div>
            <h1 className={styles.welcomeTitle}>{headline}</h1>
            <p className={styles.welcomeSub}>{cfg.welcomeSubline}</p>
            {isWelcomeThread ? (
              <div className={styles.welcomeIntro}>
                <p>
                  This is your <strong>private welcome thread</strong> — only you can see it.
                  Here's how Holler works for you:
                </p>
                <ul>
                  <li><strong>Switch roles</strong> in the top-right at any time. Each role unlocks different quick-actions and tunes how I respond.</li>
                  <li><strong>Start a new thread</strong> with the <em>+ New Chat</em> button. New threads are private to you by default.</li>
                  <li><strong>Share a thread</strong> by clicking the ⋯ on any thread you own — pick teammates by name or send the whole team a magic link.</li>
                  <li><strong>@-mention a teammate</strong> in any message and I'll pull them into the thread automatically.</li>
                  <li><strong>Email a thread</strong> by replying from your inbox — I'll thread your reply right back into the conversation.</li>
                </ul>
                <p className={styles.welcomeHint}>
                  Pick a quick action below to try me out, or just type a question.
                </p>
              </div>
            ) : (
              <p className={styles.welcomeHint}>
                Ask a question, drop in a scope, or pick a quick action below.
              </p>
            )}
            {onQuickAction && (
              <QuickActions role={role} onSelect={onQuickAction} />
            )}
          </div>
        )}
        {rows.map((row) => {
          if (row.kind === 'email') {
            return <EmailMessage key={`email-${row.email.id}`} email={row.email} />;
          }
          const msg = row.msg;
          if (msg.type === 'user') {
            return (
              <UserMessage
                key={msg.id}
                message={msg}
                isSharedThread={isSharedThread}
                currentUsername={currentUsername}
              />
            );
          }
          if (msg.type === 'foreman') {
            return (
              <ForemanMessage
                key={msg.id}
                message={msg}
                onCorrect={onCorrect}
                onAction={onAction}
                sessionKey={activeSessionId ?? null}
              />
            );
          }
          if (msg.type === 'system') {
            return <SystemMessage key={msg.id} message={msg} />;
          }
          return null;
        })}
        {isStreaming && (
          <div className={styles.typing}>
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
